import { Button } from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import {
  DataFields,
  FileUpload,
  UnprocessableFields,
} from 'api-clients/monolith';
import cx from 'classnames';
import React, { FormEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DuplicateApplicantModal } from 'components/DuplicateApplicantModal';
import { JobDescription } from 'components/JobDescription';
import { LoadingBoundary } from 'components/LoadingBoundary';
import { Snackbar } from 'components/Snackbar';
import {
  PreambleProps,
  ProgressManager,
  SubmitProps,
} from 'features/DataCollection/ProgressManager';

import { DuplicateApplicantDataType } from './constants';
import { useStyles } from './styles';

import type {
  Opening as OpeningType,
  SubmitStatus,
} from './ApplicationFormBodyRoute';

export interface ApplicationFormEmbeddedBodyProps {
  ApplicationFormQuestionsComponent: React.ComponentType<PreambleProps>;
  canReapply: boolean;
  className?: string;
  duplicateApplicantData?: DuplicateApplicantDataType;
  handleDuplicateApplicantModalClose: () => void;
  handleReapply: () => Promise<void>;
  jobDescriptionHTML: string;
  opening: OpeningType;
  sendPreviousApplicationLink: () => Promise<void>;
  setSubmitting: React.Dispatch<React.SetStateAction<SubmitStatus>>;
  Submit: React.VFC<SubmitProps>;
  submitErrorMessage: (validation?: UnprocessableFields) => JSX.Element;
  submitting: SubmitStatus;
  handleSubmit: (
    formData: FormData,
    event?: FormEvent<Element>,
  ) => Promise<void>;
  dataFields: DataFields;
  formName: (dataKey: string) => string;
  uploadFields?: FileUpload;
  handleBackToTop: () => void;
}

export const ApplicationFormEmbeddedBody: React.FC<
  ApplicationFormEmbeddedBodyProps
> = ({
  ApplicationFormQuestionsComponent,
  canReapply,
  className,
  duplicateApplicantData,
  handleDuplicateApplicantModalClose,
  handleReapply,
  jobDescriptionHTML,
  opening,
  sendPreviousApplicationLink,
  setSubmitting,
  Submit,
  submitErrorMessage,
  submitting,
  handleSubmit,
  dataFields,
  formName,
  uploadFields,
  handleBackToTop,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  return (
    <LoadingBoundary
      loading={submitting.status === 'loading'}
      className={className}
    >
      {duplicateApplicantData && (
        <DuplicateApplicantModal
          handleClose={handleDuplicateApplicantModalClose}
          {...duplicateApplicantData}
          handleCancel={canReapply ? handleReapply : undefined}
          handleSubmit={sendPreviousApplicationLink}
        />
      )}
      <div className={styles.columnLayout}>
        <Snackbar
          open={submitting.status === 'error'}
          severity="error"
          onClose={() => setSubmitting({ status: 'idle' })}
          message={submitErrorMessage(
            submitting.status === 'error' ? submitting.error : undefined,
          )}
        />
        {jobDescriptionHTML && (
          <JobDescription
            title={intl.formatMessage({
              defaultMessage: 'Job Description',
              description: 'Job Description Section Header',
            })}
            body={jobDescriptionHTML}
          />
        )}
        <div className={cx(styles.columnLayout, styles.desktopBorder)}>
          <ProgressManager
            Preamble={ApplicationFormQuestionsComponent}
            Submit={Submit}
            onSubmit={handleSubmit}
            dataFields={dataFields}
            inputName={formName}
            uploadFields={uploadFields}
            countryCode={opening.countryCode}
            serverInvalidFields={
              submitting.status === 'error' ? submitting.error : undefined
            }
          />
        </div>
        <Button
          variant="outlined"
          startIcon={<ArrowUpwardIcon />}
          onClick={handleBackToTop}
        >
          <FormattedMessage
            defaultMessage="Back to top"
            description="Button to scroll to top of page"
          />
        </Button>
      </div>
    </LoadingBoundary>
  );
};
